import React, { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useEnergyStore } from '@/stores/useEnergyStore'

interface CollapsibleCardProps {
  title: string
  subtitle?: string
  icon: LucideIcon
  iconColor?: string
  children: React.ReactNode
  defaultOpen?: boolean
  badge?: React.ReactNode
  action?: React.ReactNode
}

export const CollapsibleCard: React.FC<CollapsibleCardProps> = ({
  title,
  subtitle,
  icon: Icon,
  iconColor,
  children,
  defaultOpen = true,
  badge,
  action
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const darkMode = useEnergyStore((s) => s.darkMode)

  return (
    <div
      className={`rounded-xl md:rounded-2xl border transition-all duration-500 overflow-hidden ${
        darkMode ? 'bg-slate-900/20 border-white/5' : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center p-4 md:p-6 group transition-colors cursor-pointer gap-4 ${
          isOpen ? (darkMode ? 'bg-white/5' : 'bg-slate-50') : ''
        }`}
      >
        {/* Left Spacer */}
        <div className="flex-1 min-w-0 pointer-events-none" />

        {/* Centered Content */}
        <div className="shrink-0 max-w-[60%] sm:max-w-[70%] flex flex-col sm:flex-row items-center justify-center gap-2 md:gap-4 min-w-0">
          <div
            className={`shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-xl md:rounded-2xl flex items-center justify-center transition-all duration-500 ${
              iconColor || (darkMode ? 'bg-emerald-500/10 text-emerald-500' : 'bg-emerald-50 text-emerald-600')
            } group-hover:scale-110`}
          >
            <Icon className="w-4 h-4 md:w-5 md:h-5" />
          </div>
          
          <div className="flex flex-col items-center text-center min-w-0">
            <div className="flex flex-wrap items-center justify-center gap-1.5 md:gap-2 mb-0.5 md:mb-1 w-full">
              <h2 className={`text-[10px] sm:text-xs md:text-sm font-black uppercase tracking-widest leading-none ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {title}
              </h2>
              {badge && <div className="shrink-0 scale-90 sm:scale-100 origin-left">{badge}</div>}
            </div>
            {subtitle && (
              <p className={`text-[8px] sm:text-[9px] md:text-[10px] font-bold uppercase tracking-wider leading-tight ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Right Side: Action (if any) and Chevron */}
        <div className="flex-1 min-w-0 flex items-center justify-end gap-3">
          {action && (
            <div 
              onClick={(e) => e.stopPropagation()}
              className={`shrink-0 transition-all duration-300 ${
                isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
              }`}
            >
              {action}
            </div>
          )}
          <div
            className={`shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-500 ${
              isOpen ? 'rotate-180' : ''
            } ${
              darkMode ? 'bg-white/5 text-slate-500 group-hover:text-white' : 'bg-slate-100 text-slate-400 group-hover:text-slate-900'
            }`}
          >
            <ChevronDown size={20} />
          </div>
        </div>
      </div>

      <div
        className={`transition-all duration-700 ease-in-out overflow-hidden ${
          isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="p-4 md:p-6">
          {children}
        </div>
      </div>
    </div>
  )
}
